import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from "recharts";

const monthLabel = (m) => {
  const [y, mo] = m.split("-");
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${months[parseInt(mo)-1]} ${y.slice(2)}`;
};

export default function ForecastChart({ historical, forecast }) {
  const combined = [
    ...historical.map(h => ({ ...h, month_label: monthLabel(h.month), type: "historical" })),
    ...forecast.map(f => ({
      month: f.month, month_label: monthLabel(f.month),
      forecast: f.forecasted_demand, projected_revenue: f.revenue_forecast,
      type: "forecast",
    })),
  ];

  // Bridge: last historical point also gets forecast line
  if (historical.length > 0 && forecast.length > 0) {
    const last = combined.find(c => c.month === historical[historical.length - 1].month);
    if (last) last.forecast = last.forecast || last.actual;
  }

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-xs shadow-xl">
        <p className="text-slate-300 font-medium mb-1">{label}</p>
        {payload.map((p, i) => (
          <p key={i} style={{ color: p.color }}>{p.name}: {p.value?.toLocaleString()}</p>
        ))}
      </div>
    );
  };

  const forecastStart = forecast.length > 0 ? monthLabel(forecast[0].month) : null;

  return (
    <div className="bg-slate-900/40 border border-slate-800 rounded-2xl p-6" data-testid="forecast-chart">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Demand Forecast vs Actual</h3>
          <p className="text-xs text-slate-500 mt-0.5">12-month historical + 12-month projection</p>
        </div>
        <div className="flex items-center gap-4 text-[10px]">
          <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-cyan-400 inline-block rounded" /> Actual</span>
          <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-emerald-400 inline-block rounded" /> S&OP Forecast</span>
          <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-amber-400 inline-block rounded border border-dashed border-amber-400" /> AI Projection</span>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={320}>
        <LineChart data={combined} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
          <XAxis dataKey="month_label" tick={{ fill: '#64748b', fontSize: 10 }} axisLine={{ stroke: '#1e293b' }} interval={1} />
          <YAxis tick={{ fill: '#64748b', fontSize: 10 }} axisLine={{ stroke: '#1e293b' }} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} />
          <Tooltip content={<CustomTooltip />} />
          {forecastStart && <ReferenceLine x={forecastStart} stroke="#475569" strokeDasharray="4 4" label={{ value: "Forecast", fill: "#64748b", fontSize: 10 }} />}
          <Line type="monotone" dataKey="actual" name="Actual Demand" stroke="#22d3ee" strokeWidth={2} dot={{ r: 3, fill: '#22d3ee' }} connectNulls={false} />
          <Line type="monotone" dataKey="forecast" name="S&OP Forecast" stroke="#34d399" strokeWidth={2} dot={{ r: 3, fill: '#34d399' }} connectNulls={false} />
          <Line type="monotone" dataKey="forecasted_demand" name="AI Projection" stroke="#fbbf24" strokeWidth={2} strokeDasharray="6 3" dot={{ r: 2, fill: '#fbbf24' }} connectNulls={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
